import * as Joi from 'joi';
import { CustomHelpers } from 'joi';
import { validateNRIC } from '../../../utils/JoiUtils';

const NAME_EMPTY = 'Name is not allowed to be empty';
const NAME_MAX = 'Name is too long';
const NAME_REQUIRED = 'Name is required';

const DOCTYPE_REQUIRED = 'Document Type is required';
const CITIZENSHIP_REQUIRED = 'Citizenship is required';
const WPT_REQUIRED = 'Work Pass Type is required';

const NA = 'NOT APPLICABLE';

const VALID = 'Validate NRIC / FIN  ';
const INVALID_FIN = 'Invalid Foreign Identification No';
const INVALID_NRIC = 'Invalid NRIC';
const VALID_START_CHAR = 'Invalid FIN i.e. must start with F, G or M';

const parentParticularsExam = Joi.object({
  name: Joi.string().max(66).required().messages({
    'string.empty': NAME_EMPTY,
    'string.max': NAME_MAX,
    'any.required': NAME_REQUIRED,
  }),
  uinFinPassport: Joi.string().alphanum().required().max(30).messages({
    'any.required': 'NRIC/FIN/Passport No. is required',
    'string.empty': `NRIC/FIN/Passport No. is not allowed to be empty`,
    'string.alphanum': `NRIC/FIN/Passport No. should be alphanumeric`,
    'string.max': 'NRIC/FIN/Passport No. is too long',
  }),
  parentType: Joi.object({
    code: Joi.string(),
    description: Joi.string(),
  })
    .required()
    .messages({
      'object.base': 'Parent Details is required',
      'any.required': 'Parent Details is required',
    }),
  fatherName: Joi.when('parentType', {
    is: Joi.object({
      code: 'Mother',
      description: 'Mother',
    }),
    then: Joi.any(),
    otherwise: Joi.string().max(66).required().messages({
      'string.empty': NAME_EMPTY,
      'string.max': NAME_MAX,
      'any.required': NAME_REQUIRED,
    }),
  }),
  fatherCitizen: Joi.when('parentType', {
    is: Joi.object({
      code: 'Mother',
      description: 'Mother',
    }),
    then: Joi.any(),
    otherwise: Joi.object({
      code: Joi.string(),
      description: Joi.string(),
    })
      .required()
      .messages({
        'object.base': CITIZENSHIP_REQUIRED,
        'any.required': CITIZENSHIP_REQUIRED,
      }),
  }),
  fatherDocType: Joi.when('parentType', {
    is: Joi.object({
      code: 'Mother',
      description: 'Mother',
    }),
    then: Joi.any(),
    otherwise: Joi.object({
      code: Joi.string().required(),
      description: Joi.string().required(),
      disabled: Joi.boolean(),
    })
      .required()
      .messages({
        'object.base': DOCTYPE_REQUIRED,
        'any.required': DOCTYPE_REQUIRED,
      }),
  }),
  fatherDocNo: Joi.when('parentType', {
    is: Joi.object({
      code: 'Mother',
      description: 'Mother',
    }),
    then: Joi.any(),
    otherwise: Joi.when('fatherDocType', [
      {
        is: Joi.object({ description: 'NRIC', code: 'I', disabled: true }),
        then: Joi.string()
          .alphanum()
          .required()
          .max(20)
          .custom((value, helpers: CustomHelpers) => validateNRIC(value, helpers, true), VALID)
          .messages({
            'string.empty': `NRIC is not allowed to be empty`,
            'string.alphanum': `NRIC should be alphanumeric`,
            'string.max': 'NRIC is too long',
            'string.pattern.nric': INVALID_NRIC,
            'string.pattern.fin': 'Invalid FIN',
            'string.pattern.startCharError': 'Invalid NRIC i.e. must start with S, T',
          }),
      },
      {
        is: Joi.object({ description: 'N/A', code: 'NOTAPPLICABLE' }),
        then: Joi.any(),
        otherwise: Joi.string().alphanum().required().max(20).messages({
          'any.required': 'Passport No. is required',
          'string.empty': `Passport No. is not allowed to be empty`,
          'string.alphanum': `Passport No. should be alphanumeric`,
          'string.max': 'Passport No. is too long',
        }),
      },
    ]),
  }),
  fatherWorkPassType: Joi.when('parentType', {
    is: Joi.object({
      code: 'Mother',
      description: 'Mother',
    }),
    then: Joi.any(),
    otherwise: Joi.when('fatherCitizen', {
      is: Joi.object({
        code: 'OTHERS',
        description: 'OTHERS',
      }),
      then: Joi.object({
        code: Joi.string(),
        description: Joi.string(),
      })
        .required()
        .messages({
          'any.required': WPT_REQUIRED,
          'object.base': WPT_REQUIRED,
        }),
      otherwise: Joi.any(),
    }),
  }),
  fatherFin: Joi.when('parentType', {
    is: Joi.object({
      code: 'Mother',
      description: 'Mother',
    }),
    then: Joi.any(),
    otherwise: Joi.when('fatherCitizen', {
      is: Joi.object({
        description: 'OTHERS',
        code: 'OTHERS',
      }),
      then: Joi.when('fatherWorkPassType', {
        is: Joi.object({
          code: Joi.string(),
          description: NA,
        }),
        then: Joi.any(),
        otherwise: Joi.string()
          .alphanum()
          .required()
          .max(20)
          .custom(
            (value, helpers: CustomHelpers) => validateNRIC(value, helpers, false, true),
            VALID
          )
          .messages({
            'any.required': 'Foreign Identification No. is required',
            'string.empty': `Foreign Identification No. is not allowed to be empty`,
            'string.alphanum': `Foreign Identification No. should be alphanumeric`,
            'string.max': 'Foreign Identification No. is too long',
            'string.pattern.nric': INVALID_NRIC,
            'string.pattern.fin': INVALID_FIN,
            'string.pattern.startCharError': VALID_START_CHAR,
          }),
      }),
      otherwise: Joi.any(),
    }),
  }),
  motherName: Joi.when('parentType', {
    is: Joi.object({
      code: 'Father',
      description: 'Father',
    }),
    then: Joi.any(),
    otherwise: Joi.string().max(66).required().messages({
      'string.empty': NAME_EMPTY,
      'string.max': NAME_MAX,
      'any.required': NAME_REQUIRED,
    }),
  }),
  motherCitizen: Joi.when('parentType', {
    is: Joi.object({
      code: 'Father',
      description: 'Father',
    }),
    then: Joi.any(),
    otherwise: Joi.object({
      code: Joi.string(),
      description: Joi.string(),
    })
      .required()
      .messages({
        'any.required': CITIZENSHIP_REQUIRED,
        'object.base': CITIZENSHIP_REQUIRED,
      }),
  }),
  motherDocType: Joi.when('parentType', {
    is: Joi.object({
      code: 'Father',
      description: 'Father',
    }),
    then: Joi.any(),
    otherwise: Joi.object({
      code: Joi.string().required(),
      description: Joi.string().required(),
      disabled: Joi.boolean(),
    })
      .required()
      .messages({
        'any.required': DOCTYPE_REQUIRED,
        'object.base': DOCTYPE_REQUIRED,
      }),
  }),

  motherDocNo: Joi.when('parentType', {
    is: Joi.object({
      code: 'Father',
      description: 'Father',
    }),
    then: Joi.any(),
    otherwise: Joi.when('motherDocType', [
      {
        is: Joi.object({ description: 'NRIC', code: 'I', disabled: true }),
        then: Joi.string()
          .alphanum()
          .required()
          .max(20)
          .custom((value, helpers: CustomHelpers) => validateNRIC(value, helpers, true), VALID)
          .messages({
            'string.empty': `NRIC is not allowed to be empty`,
            'string.alphanum': `NRIC should be alphanumeric`,
            'string.max': 'NRIC is too long',
            'string.pattern.nric': INVALID_NRIC,
            'string.pattern.fin': 'Invalid FIN',
            'string.pattern.startCharError': 'Invalid NRIC i.e. must start with S, T',
          }),
      },
      {
        is: Joi.object({ description: 'N/A', code: 'NOTAPPLICABLE' }),
        then: Joi.any(),
        otherwise: Joi.string().alphanum().required().max(20).messages({
          'any.required': 'Passport No. is required',
          'string.empty': `Passport No. is not allowed to be empty`,
          'string.alphanum': `Passport No. should be alphanumeric`,
          'string.max': 'Passport No. is too long',
        }),
      },
    ]),
  }),
  motherWorkPassType: Joi.when('parentType', {
    is: Joi.object({
      code: 'Father',
      description: 'Father',
    }),
    then: Joi.any(),
    otherwise: Joi.when('motherCitizen', {
      is: Joi.object({
        description: 'OTHERS',
        code: 'OTHERS',
      }),
      then: Joi.object({
        code: Joi.string(),
        description: Joi.string(),
      })
        .required()
        .messages({
          'any.required': WPT_REQUIRED,
          'object.base': WPT_REQUIRED,
        }),
      otherwise: Joi.any(),
    }),
  }),
  motherFin: Joi.when('parentType', {
    is: Joi.object({
      code: 'Father',
      description: 'Father',
    }),
    then: Joi.any(),
    otherwise: Joi.when('motherCitizen', {
      is: Joi.object({
        description: 'OTHERS',
        code: 'OTHERS',
      }),
      then: Joi.when('motherWorkPassType', {
        is: Joi.object({
          code: Joi.string(),
          description: NA,
        }),
        then: Joi.any(),
        otherwise: Joi.string()
          .alphanum()
          .required()
          .max(20)
          .custom(
            (value, helpers: CustomHelpers) => validateNRIC(value, helpers, false, true),
            VALID
          )
          .messages({
            'any.required': 'Foreign Identification No. is required',
            'string.empty': `Foreign Identification No. is not allowed to be empty`,
            'string.alphanum': `Foreign Identification No. should be alphanumeric`,
            'string.max': 'Foreign Identification No. is too long',
            'string.pattern.nric': 'Invalid Foreign Identification No.',
            'string.pattern.fin': 'Invalid Foreign Identification No.',
            'string.pattern.startCharError': 'Invalid FIN i.e. must start with F, G or M',
          }),
      }),
      otherwise: Joi.any(),
    }),
  }),
});

export default parentParticularsExam;
